import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { createPost, getPostById, updatePost, type PostInput } from "@/lib/blog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Upload, X, ImageIcon, FileText, Settings2, Link as LinkIcon } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { marked } from "marked";
import { sanitizeQuillHtml } from "@/lib/sanitizeQuillHtml";

// Heuristic: does plain-text look like Markdown?
const looksLikeMarkdown = (s: string) =>
  /(^|\n)\s{0,3}#{1,6}\s/.test(s) ||           // headings
  /(^|\n)\s{0,3}[-*+]\s+\S/.test(s) ||         // bullet lists
  /(^|\n)\s{0,3}\d+\.\s+\S/.test(s) ||         // ordered lists
  /(^|\n)\s{0,3}>\s+/.test(s) ||               // blockquote
  /(^|\n)```/.test(s) ||                       // code fence
  /\*\*[^*\n]+\*\*/.test(s) ||                 // bold
  /(^|\W)_[^_\n]+_(\W|$)/.test(s) ||           // italic underscore
  /(^|\W)\*[^*\n]+\*(\W|$)/.test(s) ||         // italic star
  /\[[^\]]+\]\([^)]+\)/.test(s) ||             // link
  /!\[[^\]]*\]\([^)]+\)/.test(s);              // image

// Does plain-text look like raw HTML?
const looksLikeHtml = (s: string) => /<\/?[a-z][\s\S]*?>/i.test(s);

const quillModules = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link", "image"],
    [{ align: [] }],
    ["clean"],
  ],
};
const quillFormats = [
  "header", "bold", "italic", "underline", "strike",
  "list", "blockquote", "code-block", "link", "image", "align",
];

const empty: PostInput = {
  slug: "",
  title: "",
  description: "",
  date: new Date().toISOString().slice(0, 10),
  reading_minutes: 5,
  tags: [],
  category: "",
  cover_image: "",
  cover_alt: "",
  body: "",
  published: false,
};

const slugify = (s: string) =>
  s.toLowerCase().trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

function Section({
  icon: Icon,
  title,
  hint,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="px-6 pt-5 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-2 text-slate-900">
          <Icon className="w-4 h-4 text-[#de0046]" />
          <h2 className="font-serif text-lg leading-none">{title}</h2>
        </div>
        {hint && <p className="text-xs text-slate-500 mt-1.5">{hint}</p>}
      </header>
      <div className="px-6 py-5 space-y-4">{children}</div>
    </section>
  );
}

export default function AdminPostEditor() {
  const { id = "new" } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [form, setForm] = useState<PostInput>(empty);
  const [tagsText, setTagsText] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [uploadedName, setUploadedName] = useState<string>("");
  const quillRef = useRef<ReactQuill | null>(null);

  // Intercept paste so users can paste raw Markdown or HTML into the body.
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const root: HTMLElement = quill.root;

    const onPaste = (e: ClipboardEvent) => {
      const cb = e.clipboardData;
      if (!cb) return;
      const text = cb.getData("text/plain");
      if (!text) return;

      // If plain text looks like Markdown or raw HTML, convert it — even when
      // the clipboard also has a text/html flavor (browsers/editors often
      // include both, e.g. VSCode/Notion wrap markdown text in <pre> HTML).
      const plainIsMd = !!text && looksLikeMarkdown(text);
      const plainIsHtml = !!text && looksLikeHtml(text);
      if (!plainIsMd && !plainIsHtml) return; // let Quill handle normal paste

      e.preventDefault();
      e.stopImmediatePropagation();

      const converted = plainIsMd
        ? (marked.parse(text, { async: false, breaks: true, gfm: true }) as string)
        : text;

      const range = quill.getSelection(true) ?? { index: quill.getLength(), length: 0 };
      if (range.length) quill.deleteText(range.index, range.length, "user");
      quill.clipboard.dangerouslyPasteHTML(range.index, converted, "user");
    };

    root.addEventListener("paste", onPaste, true);
    return () => root.removeEventListener("paste", onPaste, true);
  }, [loading]);

  const handleCoverUpload = async (file: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `covers/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from("blog-assets").upload(path, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: file.type,
      });
      if (error) throw error;
      const { data } = supabase.storage.from("blog-assets").getPublicUrl(path);
      setForm((f) => ({ ...f, cover_image: data.publicUrl }));
      setUploadedName(file.name);
      toast.success("Image uploaded");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const p = await getPostById(id);
        if (!p) { toast.error("Post not found"); navigate("/admin"); return; }
        setForm({
          slug: p.slug, title: p.title, description: p.description, date: p.date,
          reading_minutes: p.readingMinutes, tags: p.tags, category: p.category,
          cover_image: p.coverImage, cover_alt: p.coverAlt, body: p.body, published: p.published,
        });
        setTagsText(p.tags.join(", "));
      } catch (e: any) { toast.error(e.message); }
      finally { setLoading(false); }
    })();
  }, [id, isNew, navigate]);

  const set = <K extends keyof PostInput>(k: K, v: PostInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const onTitleChange = (v: string) => {
    setForm((f) => ({
      ...f,
      title: v,
      slug: !slugTouched ? slugify(v) : f.slug,
    }));
  };

  const tagChips = useMemo(
    () => tagsText.split(",").map((s) => s.trim()).filter(Boolean),
    [tagsText]
  );

  const removeTag = (tag: string) => {
    setTagsText(tagChips.filter((t) => t !== tag).join(", "));
  };

  const wordCount = useMemo(() => {
    const text = form.body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    return text ? text.split(" ").length : 0;
  }, [form.body]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const payload: PostInput = {
      ...form,
      body: sanitizeQuillHtml(form.body),
      tags: tagChips,
    };
    try {
      if (isNew) {
        const row = await createPost(payload);
        toast.success("Created");
        navigate(`/admin/posts/${row.id}`);
      } else {
        await updatePost(id, payload);
        toast.success("Saved");
      }
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fbf9f4]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#fbf9f4] text-slate-900 pb-24 md:pb-8">
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/admin" className="text-sm text-slate-600 hover:text-[#de0046] whitespace-nowrap">← All posts</Link>
            <div className="hidden sm:block h-5 w-px bg-slate-200" />
            <h1 className="font-serif text-xl truncate">{isNew ? "New post" : form.title || "Edit post"}</h1>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <span className={`text-xs px-2 py-1 rounded-full ${form.published ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
              {form.published ? "Published" : "Draft"}
            </span>
            <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} />
            <Button form="post-form" type="submit" disabled={busy} className="bg-[#de0046] hover:bg-[#b8003a]">
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <form id="post-form" onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            <Section icon={FileText} title="Essentials" hint="The headline, URL, and summary readers see first.">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => onTitleChange(e.target.value)}
                  required
                  placeholder="A great headline"
                  className="font-serif text-xl h-12"
                />
              </div>

              <div className="space-y-2">
                <Label>Slug</Label>
                <div className="flex items-stretch rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                  <span className="inline-flex items-center px-3 text-xs text-slate-500 bg-slate-50 border-r border-input">
                    /blog/
                  </span>
                  <input
                    value={form.slug}
                    onChange={(e) => { setSlugTouched(true); set("slug", e.target.value); }}
                    required
                    pattern="[a-z0-9-]+"
                    placeholder="lowercase-kebab"
                    className="flex-1 px-3 py-2 text-sm bg-transparent outline-none"
                  />
                </div>
                <p className="text-xs text-slate-500">Auto-generated from title until you edit it.</p>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  required
                  rows={3}
                  placeholder="A short summary shown in listings and search results."
                />
              </div>
            </Section>

            <Section icon={ImageIcon} title="Cover image" hint="Shown at the top of the post and in social previews.">
              {form.cover_image ? (
                <div className="space-y-3">
                  <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50 group">
                    <img
                      src={form.cover_image}
                      alt={form.cover_alt || "Cover preview"}
                      className="w-full max-h-72 object-cover"
                    />
                    <div className="absolute top-3 right-3 flex gap-2">
                      <label className="bg-white/95 hover:bg-white text-xs px-3 py-1.5 rounded-md shadow cursor-pointer text-slate-700 font-medium">
                        Replace
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={uploading}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleCoverUpload(f);
                            e.target.value = "";
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => { set("cover_image", ""); setUploadedName(""); }}
                        className="bg-white/95 hover:bg-white rounded-md p-1.5 shadow"
                        aria-label="Remove cover image"
                      >
                        <X className="w-4 h-4 text-slate-700" />
                      </button>
                    </div>
                  </div>
                  {uploadedName && <p className="text-xs text-slate-500 truncate">Uploaded: {uploadedName}</p>}
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg p-10 bg-slate-50/60 hover:bg-slate-50 hover:border-[#de0046]/40 cursor-pointer transition">
                  <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center">
                    <Upload className="w-5 h-5 text-slate-400" />
                  </div>
                  <span className="text-sm text-slate-700 font-medium">
                    {uploading ? "Uploading…" : "Drop image or click to upload"}
                  </span>
                  <span className="text-xs text-slate-400">PNG, JPG, or WebP up to ~5MB</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleCoverUpload(f);
                      e.target.value = "";
                    }}
                  />
                </label>
              )}

              <div className="space-y-2">
                <Label>Alt text</Label>
                <Input
                  value={form.cover_alt}
                  onChange={(e) => set("cover_alt", e.target.value)}
                  required
                  placeholder="Describe the image for screen readers"
                />
              </div>

              <details className="group">
                <summary className="text-xs text-slate-500 cursor-pointer inline-flex items-center gap-1.5 hover:text-slate-700 list-none">
                  <LinkIcon className="w-3.5 h-3.5" />
                  <span className="underline-offset-2 group-hover:underline">Use an external image URL instead</span>
                </summary>
                <Input
                  value={form.cover_image}
                  onChange={(e) => set("cover_image", e.target.value)}
                  placeholder="https://…"
                  className="mt-2 text-sm"
                />
              </details>
            </Section>

            <Section icon={FileText} title="Content" hint="The body of your post. Supports rich formatting.">
              <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={form.body}
                  onChange={(v) => set("body", v)}
                  modules={quillModules}
                  formats={quillFormats}
                  className="[&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:!border-b-slate-200 [&_.ql-container]:!border-0 [&_.ql-editor]:min-h-[460px] [&_.ql-editor]:font-serif [&_.ql-editor]:text-base"
                />
              </div>
              <p className="text-xs text-slate-500">{wordCount} {wordCount === 1 ? "word" : "words"}</p>
            </Section>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-6">
            <Section icon={Settings2} title="Publish" hint="Visibility and scheduling.">
              <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium">{form.published ? "Published" : "Draft"}</p>
                  <p className="text-xs text-slate-500">{form.published ? "Visible on the blog" : "Only you can see this"}</p>
                </div>
                <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Read time</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min={1}
                      value={form.reading_minutes}
                      onChange={(e) => set("reading_minutes", parseInt(e.target.value) || 1)}
                      className="pr-12"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">min</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Input value={form.category} onChange={(e) => set("category", e.target.value)} required placeholder="e.g. History" />
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <Input
                  value={tagsText}
                  onChange={(e) => setTagsText(e.target.value)}
                  placeholder="wordle, history, puzzles"
                />
                {tagChips.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {tagChips.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="group inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                      >
                        {tag}
                        <X className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500">Separate with commas. Click a tag to remove it.</p>
              </div>
            </Section>
          </aside>
        </form>
      </main>

      {/* Mobile sticky save bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between gap-3 z-20">
        <div className="flex items-center gap-2">
          <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} />
          <span className="text-xs text-slate-600">{form.published ? "Published" : "Draft"}</span>
        </div>
        <Button form="post-form" type="submit" disabled={busy} className="bg-[#de0046] hover:bg-[#b8003a] flex-1 max-w-[180px]">
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
